import { defineStore } from "pinia";
import { ref } from "vue";
import { useUserStore } from "./user";
import { type ThirdAccount } from "@/api/modules/auth";

export type AccountSettingSection = "subscribe" | "points" | "bind" | "recharge";

/**
 * UI 状态管理
 * @description 管理全局 UI 状态，如弹窗显示/隐藏等
 */
export const useUIStore = defineStore("ui", () => {
  /**
   * 账号设置弹窗相关
   */
  const accountSettingVisible = ref(false);
  const accountSettingSection = ref<AccountSettingSection>("subscribe");
  
  /**
   * 显示账号设置弹窗
   */
  const showAccountSettingDialog = (section: AccountSettingSection) => {
    accountSettingVisible.value = true;
    accountSettingSection.value = section;
  };

  /**
   * 隐藏账号设置弹窗
   */
  const hideAccountSettingDialog = () => {
    accountSettingVisible.value = false;
  };

  /**
   * 绑定手机号和邮箱弹窗显示状态
   */
  const bindEmailPhoneOptions = ref({
    visible: false,
    type: "email",
    closeable: true,
    oldPhone: "",
    oldPhoneAreaCode: "",
    oldEmail: "",
  });

  /**
   * 显示绑定手机号和邮箱弹窗
   */
  const showBindEmailPhoneDialog = (type: "email" | "phone", closeable?: boolean) => {
    bindEmailPhoneOptions.value.closeable = closeable ?? true;
    bindEmailPhoneOptions.value.type = type;
    bindEmailPhoneOptions.value.visible = true;
    bindEmailPhoneOptions.value.oldPhone = useUserStore().userInfo?.mobile_number || "";
    bindEmailPhoneOptions.value.oldPhoneAreaCode = useUserStore().userInfo?.mobile_prefix || "";
    bindEmailPhoneOptions.value.oldEmail = useUserStore().userInfo?.email || "";
  };

  /**
   * 隐藏绑定手机号和邮箱弹窗
   */
  const hideBindEmailPhoneDialog = () => {
    bindEmailPhoneOptions.value.visible = false;
  };

  /**
   * 积分购买弹窗显示状态
   */
  const pointsPurchaseVisible = ref(false);

  /**
   * 显示积分购买弹窗
   */
  const showPointsPurchaseDialog = () => {
    pointsPurchaseVisible.value = true;
  };

  /**
   * 隐藏积分购买弹窗
   */
  const hidePointsPurchaseDialog = () => {
    pointsPurchaseVisible.value = false;
  };

  /**
   * 解绑账号授权弹窗
   */
  const unbindAuthOptions = ref({
    visible: false,
    account: {} as ThirdAccount,
  });

  /**
   * 显示解绑账号授权弹窗
   */
  const showUnbindAuthDialog = (account: ThirdAccount) => {
    unbindAuthOptions.value.account = account;
    unbindAuthOptions.value.visible = true;
  };

  /**
   * 隐藏解绑账号授权弹窗
   */
  const hideUnbindAuthDialog = () => {
    unbindAuthOptions.value.visible = false;
    unbindAuthOptions.value.account = {} as ThirdAccount;
  };

  return {
    accountSettingVisible,
    accountSettingSection,
    showAccountSettingDialog,
    hideAccountSettingDialog,
    bindEmailPhoneOptions,
    showBindEmailPhoneDialog,
    hideBindEmailPhoneDialog,
    pointsPurchaseVisible,
    showPointsPurchaseDialog,
    hidePointsPurchaseDialog,
    showUnbindAuthDialog,
    hideUnbindAuthDialog,
    unbindAuthOptions,
  };
});
